import React, { useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { FetchResult } from '@apollo/client'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import AccessCodeForm from '@sputnik/components/PhoneVerificationModalContainer/AccessCodeForm'
import { RequestPhoneVerification_Mutation } from '@sputnik/pages/gql/mutations/RequestPhoneVerification.gql'
import { useSubmitPhoneVerificationCode } from '@sputnik/pages/gql/mutations/SubmitPhoneVerificationCode.gql'
import { logError, logInfo, logWarning } from '@sputnik/utils/loggerUtils'

import SmsAccessCodeModalContent from './SmsAccessCodeModalContent'

interface SmsAccessCodeFormProps {
  isLoadingResendCode: boolean
  submitPageMutation?: () => void
  requestPhoneVerification: () => Promise<FetchResult<RequestPhoneVerification_Mutation>>
}

const SmsAccessCodeForm = ({
  submitPageMutation,
  requestPhoneVerification,
  isLoadingResendCode,
}: SmsAccessCodeFormProps) => {
  const [hasError, setHasError] = useState(false)
  const { appId } = useParams()
  const { state } = useLocation()
  const showDangerToast = useToast(TOAST_TYPE_DANGER) as any
  const showSuccessToast = useToast(TOAST_TYPE_SUCCESS) as any
  const [searchParams, setSearchParams] = useSearchParams()
  const phoneNumber = state?.phoneNumber
  const phoneLastFour = phoneNumber?.slice(-4)
  const authorizedSignerId = state?.authorizedSignerId

  const [submitPhoneVerificationCode, { loading }] = useSubmitPhoneVerificationCode({
    onCompleted: () => {
      logInfo('handleSubmitSmsCode', {
        attributes: {
          creditAppId: appId,
          result: 'Send code button has been clicked',
        },
        eventName: 'SubmitPhoneVerificationCode',
      })

      searchParams.delete('verifySmsCodePending')
      setSearchParams(Object.fromEntries(searchParams))
      showSuccessToast(
        <FormattedMessage
          defaultMessage='Phone number has been verified'
          id='sputnik.SmsAccessCodeForm__phoneVerified'
        />,
      )
      submitPageMutation?.()
    },
    onError: (e) => {
      logWarning('Error submitting phone verification code', {
        attributes: {
          action: 'handleSubmitSmsCode',
          creditAppId: appId,
          message: e?.message,
        },
        eventName: 'SubmitPhoneVerificationCode',
      })
      setHasError(true)
    },
  })

  const handleResendClick = () => {
    requestPhoneVerification()
      .then(() => {
        logInfo('handleResendClick', {
          attributes: {
            result: 'Resend auth code clicked',
            creditAppId: appId,
            mfaMethod: 'sms',
          },
          eventName: 'ResendPhoneVerificationCode',
        })
        showSuccessToast(
          <FormattedMessage
            defaultMessage='Code has been resent'
            id='sputnik.SmsAccessCodeForm__codeResent'
          />,
        )
      })
      .catch((e) => {
        logError(e, {
          attributes: {
            action: 'handleResendClick',
            creditAppId: appId,
            result: 'Error resending code',
            mfaMethod: 'sms',
          },
          eventName: 'ResendPhoneVerificationCode',
        })
        showDangerToast(
          <FormattedMessage
            defaultMessage='Error resending code'
            id='sputnik.SmsAccessCodeForm__errorResendingCode'
          />,
        )
      })
  }

  const handleSubmitAccessCode = (accessCode: string) => {
    logInfo('handleSubmitAccessCode', {
      attributes: {
        result: 'Verify code button clicked',
        creditAppId: appId,
        mfaMethod: 'sms',
      },
      eventName: 'SubmitPhoneVerificationCode',
    })

    submitPhoneVerificationCode({
      variables: {
        salesforceCreditId: appId || '',
        phoneNumber,
        personId: authorizedSignerId,
        accessCode,
      },
    })
  }

  const processDescirption = (
    <FormattedMessage
      defaultMessage='We sent a code to your phone number ending with {phoneLastFour} to make sure it’s you.'
      id='sputnik.SmsAccessCodeForm__yg/OcB'
      values={{ phoneLastFour }}
    />
  )

  return (
    <>
      <AccessCodeForm
        disableError={() => setHasError(false)}
        handleSubmitAccessCode={handleSubmitAccessCode}
      >
        <SmsAccessCodeModalContent
          handleResendClick={handleResendClick}
          handleSubmitAccessCode={handleSubmitAccessCode}
          hasError={hasError}
          loadingResendCode={isLoadingResendCode}
          loadingSubmitCode={loading}
          processDescription={processDescirption}
        />
      </AccessCodeForm>
    </>
  )
}

export default SmsAccessCodeForm
